'use client';
import { motion } from 'framer-motion';
import { useRouter } from 'next/navigation';
import React, {
  createContext,
  MouseEventHandler,
  PropsWithChildren,
  useContext,
} from 'react';
import { useTransition } from 'react';

import { EXIT_DELAY_IN_MILLISECONDS } from './settings';

type TransitionContext = {
  pending: boolean;
  navigate: (url: string) => void;
};

const Context = createContext<TransitionContext>({
  pending: false,
  navigate: () => {},
});

export const usePageTransition = () => useContext(Context);

const Transitions = ({ children }: PropsWithChildren) => {
  const [pending, start] = useTransition();
  const router = useRouter();

  const navigate = (href: string) => {
    start(async () => {
      router.push(href);
      await new Promise<void>(resolve =>
        setTimeout(resolve, EXIT_DELAY_IN_MILLISECONDS)
      );
    });
  };

  const onClick: MouseEventHandler<HTMLDivElement> = event => {
    const a = (event.target as Element).closest('a');

    if (a) {
      const targetBlank = a.getAttribute('target') === '_blank';

      if (!targetBlank) {
        event.preventDefault();
        const href = a.getAttribute('href');
        if (href) navigate(href);
      }
    }
  };

  return (
    <Context.Provider value={{ pending, navigate }}>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 0.1 }}
        onClickCapture={onClick}
      >
        {children}
      </motion.div>
    </Context.Provider>
  );
};

export default Transitions;
